"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import useEmblaCarousel from "embla-carousel-react";
import AutoplayPlugin from "embla-carousel-autoplay";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { cn } from "../utils";
import { Icon } from "../icon";
import { IconButton } from "../icon-button";
import { carouselVariants, carouselContentVariants, carouselItemVariants, carouselDotIndicatorVariants, carouselDotVariants, carouselFullscreenOverlayVariants } from "./carousel.variants";
import type { CarouselProps, CarouselContextValue, CarouselContentProps, CarouselItemProps, CarouselNavigationProps, CarouselDotsProps, CarouselFullscreenTriggerProps, CarouselNextWithProgressProps, CarouselProgressBarProps, CarouselNavButtonsProps } from "./carousel.types";
const CarouselContext = React.createContext<CarouselContextValue | null>(null);
function useCarousel() {
  const context = React.useContext(CarouselContext);
  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }
  return context;
}
const Carousel = React.forwardRef<HTMLDivElement, CarouselProps>((t0, ref) => {
  const $ = _c(93);
  let children;
  let className;
  let duration;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== t0) {
    ({
      orientation: t1,
      autoplay: t2,
      loop: t3,
      align: t4,
      duration,
      dragFree: t5,
      wheelGestures: t6,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = duration;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    children = $[1];
    className = $[2];
    duration = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  const orientation = t1 === undefined ? "horizontal" : t1;
  const autoplay = t2 === undefined ? false : t2;
  const loop = t3 === undefined ? false : t3;
  const align = t4 === undefined ? "start" : t4;
  const dragFree = t5 === undefined ? false : t5;
  const wheelGestures = t6 === undefined ? false : t6;
  const autoplayEnabled = Boolean(autoplay);
  const autoplayDelay = typeof autoplay === "object" ? autoplay.delay ?? 4000 : 4000;
  const autoplayStopOnMouseEnter = typeof autoplay === "object" ? autoplay.stopOnMouseEnter ?? true : true;
  const autoplayStopOnInteraction = typeof autoplay === "object" ? autoplay.stopOnInteraction ?? false : false;
  let list;
  if ($[11] !== autoplayDelay || $[12] !== autoplayEnabled || $[13] !== autoplayStopOnInteraction || $[14] !== autoplayStopOnMouseEnter || $[15] !== wheelGestures) {
    list = [];
    if (autoplayEnabled) {
      let t7;
      if ($[17] !== autoplayDelay || $[18] !== autoplayStopOnInteraction || $[19] !== autoplayStopOnMouseEnter) {
        t7 = AutoplayPlugin({
          delay: autoplayDelay,
          stopOnMouseEnter: autoplayStopOnMouseEnter,
          stopOnInteraction: autoplayStopOnInteraction
        });
        $[17] = autoplayDelay;
        $[18] = autoplayStopOnInteraction;
        $[19] = autoplayStopOnMouseEnter;
        $[20] = t7;
      } else {
        t7 = $[20];
      }
      list.push(t7);
    }
    if (wheelGestures) {
      let t7;
      if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = WheelGesturesPlugin();
        $[21] = t7;
      } else {
        t7 = $[21];
      }
      list.push(t7);
    }
    $[11] = autoplayDelay;
    $[12] = autoplayEnabled;
    $[13] = autoplayStopOnInteraction;
    $[14] = autoplayStopOnMouseEnter;
    $[15] = wheelGestures;
    $[16] = list;
  } else {
    list = $[16];
  }
  const plugins = list;
  const t7 = orientation === "vertical" ? "y" : "x";
  let t8;
  if ($[22] !== loop || $[23] !== orientation) {
    t8 = orientation === "vertical" ? {
      containScroll: false
    } : !loop && {
      containScroll: "trimSnaps"
    };
    $[22] = loop;
    $[23] = orientation;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== duration) {
    t9 = duration !== undefined && {
      duration
    };
    $[25] = duration;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== align || $[28] !== dragFree || $[29] !== loop || $[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t10 = {
      axis: t7,
      loop,
      align,
      dragFree,
      ...t8,
      ...t9
    };
    $[27] = align;
    $[28] = dragFree;
    $[29] = loop;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(t10, plugins);
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  let t11;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [];
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const [scrollSnaps, setScrollSnaps] = React.useState(t11);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  let t12;
  if ($[35] !== emblaApi) {
    t12 = () => {
      emblaApi?.scrollPrev();
    };
    $[35] = emblaApi;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const scrollPrev = t12;
  let t13;
  if ($[37] !== emblaApi) {
    t13 = () => {
      emblaApi?.scrollNext();
    };
    $[37] = emblaApi;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const scrollNext = t13;
  let t14;
  if ($[39] !== emblaApi) {
    t14 = index => {
      emblaApi?.scrollTo(index);
    };
    $[39] = emblaApi;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const scrollTo = t14;
  let t15;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setIsFullscreen(true);
    };
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  const enterFullscreen = t15;
  let t16;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      setIsFullscreen(false);
    };
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  const exitFullscreen = t16;
  let t17;
  if ($[43] !== emblaApi) {
    t17 = () => {
      if (!emblaApi) {
        return;
      }
      setSelectedIndex(emblaApi.selectedScrollSnap());
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    $[43] = emblaApi;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  const onSelect = t17;
  let t18;
  if ($[45] !== emblaApi || $[46] !== onSelect) {
    t18 = () => {
      if (!emblaApi) {
        return;
      }
      setScrollSnaps(emblaApi.scrollSnapList());
      onSelect();
    };
    $[45] = emblaApi;
    $[46] = onSelect;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  const onInit = t18;
  let t19;
  let t20;
  if ($[48] !== emblaApi || $[49] !== onInit || $[50] !== onSelect) {
    t19 = () => {
      if (!emblaApi) {
        return;
      }
      onInit();
      emblaApi.on("select", onSelect);
      emblaApi.on("reInit", onInit);
      const frame = requestAnimationFrame(() => {
        emblaApi.reInit();
      });
      return () => {
        cancelAnimationFrame(frame);
        emblaApi.off("select", onSelect);
        emblaApi.off("reInit", onInit);
      };
    };
    t20 = [emblaApi, onSelect, onInit];
    $[48] = emblaApi;
    $[49] = onInit;
    $[50] = onSelect;
    $[51] = t19;
    $[52] = t20;
  } else {
    t19 = $[51];
    t20 = $[52];
  }
  React.useEffect(t19, t20);
  let t21;
  let t22;
  if ($[53] !== isFullscreen) {
    t21 = () => {
      if (!isFullscreen) {
        return;
      }
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const handleEscape = event => {
        if (event.key === "Escape") {
          exitFullscreen();
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.body.style.overflow = prevOverflow;
        document.removeEventListener("keydown", handleEscape);
      };
    };
    t22 = [isFullscreen, exitFullscreen];
    $[53] = isFullscreen;
    $[54] = t21;
    $[55] = t22;
  } else {
    t21 = $[54];
    t22 = $[55];
  }
  React.useEffect(t21, t22);
  let t23;
  if ($[56] !== orientation || $[57] !== scrollNext || $[58] !== scrollPrev) {
    t23 = event_0 => {
      const isHorizontal = orientation !== "vertical";
      if (isHorizontal && event_0.key === "ArrowLeft" || !isHorizontal && event_0.key === "ArrowUp") {
        event_0.preventDefault();
        scrollPrev();
      } else {
        if (isHorizontal && event_0.key === "ArrowRight" || !isHorizontal && event_0.key === "ArrowDown") {
          event_0.preventDefault();
          scrollNext();
        }
      }
    };
    $[56] = orientation;
    $[57] = scrollNext;
    $[58] = scrollPrev;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  const handleKeyDown = t23;
  let t24;
  if ($[60] !== canScrollNext || $[61] !== canScrollPrev || $[62] !== emblaApi || $[63] !== emblaRef || $[64] !== isFullscreen || $[65] !== orientation || $[66] !== scrollNext || $[67] !== scrollPrev || $[68] !== scrollSnaps || $[69] !== scrollTo || $[70] !== selectedIndex) {
    t24 = {
      carouselRef: emblaRef,
      api: emblaApi,
      canScrollPrev,
      canScrollNext,
      scrollPrev,
      scrollNext,
      selectedIndex,
      scrollSnaps,
      scrollTo,
      orientation,
      isFullscreen,
      enterFullscreen,
      exitFullscreen
    };
    $[60] = canScrollNext;
    $[61] = canScrollPrev;
    $[62] = emblaApi;
    $[63] = emblaRef;
    $[64] = isFullscreen;
    $[65] = orientation;
    $[66] = scrollNext;
    $[67] = scrollPrev;
    $[68] = scrollSnaps;
    $[69] = scrollTo;
    $[70] = selectedIndex;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  const contextValue = t24;
  let t25;
  if ($[72] !== isFullscreen) {
    t25 = isFullscreen ? cn(carouselFullscreenOverlayVariants()) : "contents";
    $[72] = isFullscreen;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  let t26;
  if ($[74] !== isFullscreen) {
    t26 = isFullscreen && <button type="button" className="absolute right-4 top-4 z-10 inline-flex size-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20" onClick={exitFullscreen} aria-label="Exit fullscreen"><Icon name="close" className="size-6" /></button>;
    $[74] = isFullscreen;
    $[75] = t26;
  } else {
    t26 = $[75];
  }
  let t27;
  if ($[76] !== className || $[77] !== isFullscreen || $[78] !== orientation) {
    t27 = cn(isFullscreen ? "relative mx-auto w-full max-w-7xl px-4" : carouselVariants({
      orientation
    }), className);
    $[76] = className;
    $[77] = isFullscreen;
    $[78] = orientation;
    $[79] = t27;
  } else {
    t27 = $[79];
  }
  let t28;
  if ($[80] !== children || $[81] !== handleKeyDown || $[82] !== props || $[83] !== ref || $[84] !== t27) {
    t28 = <div ref={ref} className={t27} role="region" aria-roledescription="carousel" tabIndex={0} onKeyDown={handleKeyDown} {...props}>{children}</div>;
    $[80] = children;
    $[81] = handleKeyDown;
    $[82] = props;
    $[83] = ref;
    $[84] = t27;
    $[85] = t28;
  } else {
    t28 = $[85];
  }
  let t29;
  if ($[86] !== t25 || $[87] !== t26 || $[88] !== t28) {
    t29 = <div className={t25}>{t26}{t28}</div>;
    $[86] = t25;
    $[87] = t26;
    $[88] = t28;
    $[89] = t29;
  } else {
    t29 = $[89];
  }
  let t30;
  if ($[90] !== contextValue || $[91] !== t29) {
    t30 = <CarouselContext value={contextValue}>{t29}</CarouselContext>;
    $[90] = contextValue;
    $[91] = t29;
    $[92] = t30;
  } else {
    t30 = $[92];
  }
  return t30;
});
Carousel.displayName = "Carousel";
const CarouselContent = React.forwardRef<HTMLDivElement, CarouselContentProps>((t0, ref) => {
  const $ = _c(18);
  let className;
  let props;
  let viewportClassName;
  if ($[0] !== t0) {
    ({
      className,
      viewportClassName,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = viewportClassName;
  } else {
    className = $[1];
    props = $[2];
    viewportClassName = $[3];
  }
  const {
    carouselRef,
    orientation
  } = useCarousel();
  const t1 = orientation === "vertical" && "h-full";
  let t2;
  if ($[4] !== t1 || $[5] !== viewportClassName) {
    t2 = cn("overflow-hidden", t1, viewportClassName);
    $[4] = t1;
    $[5] = viewportClassName;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== className || $[8] !== orientation) {
    t3 = cn(carouselContentVariants({
      orientation
    }), className);
    $[7] = className;
    $[8] = orientation;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== props || $[11] !== ref || $[12] !== t3) {
    t4 = <div ref={ref} className={t3} {...props} />;
    $[10] = props;
    $[11] = ref;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== carouselRef || $[15] !== t2 || $[16] !== t4) {
    t5 = <div ref={carouselRef} className={t2}>{t4}</div>;
    $[14] = carouselRef;
    $[15] = t2;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
});
CarouselContent.displayName = "CarouselContent";
const CarouselItem = React.forwardRef<HTMLDivElement, CarouselItemProps>((t0, ref) => {
  const $ = _c(10);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    orientation
  } = useCarousel();
  let t1;
  if ($[3] !== className || $[4] !== orientation) {
    t1 = cn(carouselItemVariants({
      orientation
    }), className);
    $[3] = className;
    $[4] = orientation;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <div ref={ref} role="group" aria-roledescription="slide" className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
});
CarouselItem.displayName = "CarouselItem";
const CarouselPrevious = React.forwardRef<HTMLButtonElement, CarouselNavigationProps>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    canScrollPrev,
    scrollPrev
  } = useCarousel();
  const t1 = !canScrollPrev;
  let t2;
  if ($[3] !== className || $[4] !== props || $[5] !== ref || $[6] !== scrollPrev || $[7] !== t1) {
    t2 = <IconButton ref={ref} className={className} disabled={t1} onClick={scrollPrev} aria-label="Previous slide" icon="arrow-left" variant="white" {...props} />;
    $[3] = className;
    $[4] = props;
    $[5] = ref;
    $[6] = scrollPrev;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
CarouselPrevious.displayName = "CarouselPrevious";
const CarouselNext = React.forwardRef<HTMLButtonElement, CarouselNavigationProps>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    canScrollNext,
    scrollNext
  } = useCarousel();
  const t1 = !canScrollNext;
  let t2;
  if ($[3] !== className || $[4] !== props || $[5] !== ref || $[6] !== scrollNext || $[7] !== t1) {
    t2 = <IconButton ref={ref} className={className} disabled={t1} onClick={scrollNext} aria-label="Next slide" icon="arrow-right" variant="white" {...props} />;
    $[3] = className;
    $[4] = props;
    $[5] = ref;
    $[6] = scrollNext;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
CarouselNext.displayName = "CarouselNext";
const PROGRESS_SIZE = 44;
const PROGRESS_DEFAULT_STROKE = 1;
const CarouselNextWithProgress = React.forwardRef<HTMLButtonElement, CarouselNextWithProgressProps>((t0, ref) => {
  const $ = _c(36);
  let className;
  let props;
  let size;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let wrapperClassName;
  if ($[0] !== t0) {
    ({
      className,
      autoplayDelay: t1,
      strokeWidth: t2,
      strokeColor: t3,
      variant: t4,
      size,
      wrapperClassName,
      noAutoplayAdvance: t5,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = size;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = wrapperClassName;
  } else {
    className = $[1];
    props = $[2];
    size = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    wrapperClassName = $[9];
  }
  const autoplayDelay = t1 === undefined ? 4000 : t1;
  const strokeWidth = t2 === undefined ? PROGRESS_DEFAULT_STROKE : t2;
  const strokeColor = t3 === undefined ? "currentColor" : t3;
  const variant = t4 === undefined ? "white" : t4;
  const noAutoplayAdvance = t5 === undefined ? false : t5;
  const {
    canScrollNext,
    scrollNext,
    api
  } = useCarousel();
  const circleRef = React.useRef(null);
  const animRef = React.useRef(null);
  const radius = (PROGRESS_SIZE - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let t6;
  let t7;
  if ($[10] !== api || $[11] !== autoplayDelay || $[12] !== circumference || $[13] !== noAutoplayAdvance || $[14] !== scrollNext) {
    t6 = () => {
      if (!api || !circleRef.current) {
        return;
      }
      const circle = circleRef.current;
      const startProgress = () => {
        animRef.current?.cancel();
        const anim = circle.animate([{
          strokeDashoffset: String(circumference)
        }, {
          strokeDashoffset: "0"
        }], {
          duration: autoplayDelay,
          easing: "linear",
          fill: "forwards"
        });
        anim.onfinish = () => {
          if (!noAutoplayAdvance) {
            scrollNext();
          }
        };
        animRef.current = anim;
      };
      const resetProgress = () => {
        animRef.current?.cancel();
        circle.style.strokeDashoffset = String(circumference);
      };
      const onSelect = () => {
        resetProgress();
      };
      const onSettle = () => {
        startProgress();
      };
      startProgress();
      api.on("select", onSelect);
      api.on("settle", onSettle);
      return () => {
        animRef.current?.cancel();
        api.off("select", onSelect);
        api.off("settle", onSettle);
      };
    };
    t7 = [api, autoplayDelay, circumference, scrollNext, noAutoplayAdvance];
    $[10] = api;
    $[11] = autoplayDelay;
    $[12] = circumference;
    $[13] = noAutoplayAdvance;
    $[14] = scrollNext;
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  React.useEffect(t6, t7);
  let t8;
  if ($[17] !== wrapperClassName) {
    t8 = cn("relative inline-flex items-center justify-center size-11", wrapperClassName);
    $[17] = wrapperClassName;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== circumference || $[20] !== radius || $[21] !== strokeColor || $[22] !== strokeWidth) {
    t9 = <svg className="pointer-events-none absolute inset-0 size-full" viewBox={`0 0 ${PROGRESS_SIZE} ${PROGRESS_SIZE}`} fill="none"><circle ref={circleRef} cx={PROGRESS_SIZE / 2} cy={PROGRESS_SIZE / 2} r={radius} stroke={strokeColor} strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={circumference} strokeLinecap="round" transform={`rotate(-90 ${PROGRESS_SIZE / 2} ${PROGRESS_SIZE / 2})`} /></svg>;
    $[19] = circumference;
    $[20] = radius;
    $[21] = strokeColor;
    $[22] = strokeWidth;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const t10 = !canScrollNext;
  let t11;
  if ($[24] !== className || $[25] !== props || $[26] !== ref || $[27] !== scrollNext || $[28] !== size || $[29] !== t10 || $[30] !== variant) {
    t11 = <IconButton ref={ref} className={className} disabled={t10} onClick={scrollNext} aria-label="Next slide" icon="arrow-right" variant={variant} size={size} {...props} />;
    $[24] = className;
    $[25] = props;
    $[26] = ref;
    $[27] = scrollNext;
    $[28] = size;
    $[29] = t10;
    $[30] = variant;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t11 || $[33] !== t8 || $[34] !== t9) {
    t12 = <div className={t8}>{t9}{t11}</div>;
    $[32] = t11;
    $[33] = t8;
    $[34] = t9;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
});
CarouselNextWithProgress.displayName = "CarouselNextWithProgress";
function CarouselProgressBar(t0) {
  const $ = _c(13);
  const {
    className,
    indicatorClassName
  } = t0;
  const {
    selectedIndex,
    scrollSnaps
  } = useCarousel();
  if (scrollSnaps.length <= 1) {
    return null;
  }
  const total = scrollSnaps.length;
  const indicatorWidthPct = 100 / total;
  const indicatorLeftPct = selectedIndex / total * 100;
  let t1;
  if ($[0] !== className) {
    t1 = cn("relative overflow-hidden rounded-full", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== indicatorClassName) {
    t2 = cn("absolute rounded-full transition-[left] duration-300 ease-out", indicatorClassName);
    $[2] = indicatorClassName;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `${indicatorWidthPct}%`;
  const t4 = `${indicatorLeftPct}%`;
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = {
      width: t3,
      left: t4
    };
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t2 || $[8] !== t5) {
    t6 = <div className={t2} style={t5} />;
    $[7] = t2;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t1 || $[11] !== t6) {
    t7 = <div className={t1}>{t6}</div>;
    $[10] = t1;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
const NAV_BREAKPOINT_CLASSES = {
  mobile: {
    small: "mobile:hidden",
    large: "hidden mobile:inline-flex"
  },
  desktop: {
    small: "desktop:hidden",
    large: "hidden desktop:inline-flex"
  }
} as const;
function CarouselNavButtons(t0) {
  const $ = _c(27);
  const {
    breakpoint,
    variant: t1,
    autoplayDelay: t2,
    className
  } = t0;
  const variant = t1 === undefined ? "white" : t1;
  const autoplayDelay = t2 === undefined ? 6000 : t2;
  const {
    canScrollPrev,
    scrollPrev
  } = useCarousel();
  const {
    small: smallToggle,
    large: largeToggle
  } = NAV_BREAKPOINT_CLASSES[breakpoint];
  let t3;
  if ($[0] !== className) {
    t3 = cn("flex items-center gap-2", className);
    $[0] = className;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = !canScrollPrev;
  let t5;
  if ($[2] !== scrollPrev || $[3] !== smallToggle || $[4] !== t4) {
    t5 = <IconButton variant="white" size="sm" icon="arrow-left" aria-label="Previous slide" disabled={t4} onClick={scrollPrev} className={smallToggle} />;
    $[2] = scrollPrev;
    $[3] = smallToggle;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = !canScrollPrev;
  let t7;
  if ($[6] !== largeToggle || $[7] !== scrollPrev || $[8] !== t6 || $[9] !== variant) {
    t7 = <IconButton variant={variant} icon="arrow-left" aria-label="Previous slide" disabled={t6} onClick={scrollPrev} className={largeToggle} />;
    $[6] = largeToggle;
    $[7] = scrollPrev;
    $[8] = t6;
    $[9] = variant;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== smallToggle) {
    t8 = cn("size-8", smallToggle);
    $[11] = smallToggle;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== autoplayDelay || $[14] !== t8 || $[15] !== variant) {
    t9 = <CarouselNextWithProgress autoplayDelay={autoplayDelay} variant={variant} size="sm" wrapperClassName={t8} noAutoplayAdvance={true} />;
    $[13] = autoplayDelay;
    $[14] = t8;
    $[15] = variant;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== autoplayDelay || $[18] !== largeToggle || $[19] !== variant) {
    t10 = <CarouselNextWithProgress autoplayDelay={autoplayDelay} variant={variant} wrapperClassName={largeToggle} />;
    $[17] = autoplayDelay;
    $[18] = largeToggle;
    $[19] = variant;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t3 || $[23] !== t5 || $[24] !== t7 || $[25] !== t9) {
    t11 = <div className={t3}>{t5}{t7}{t9}{t10}</div>;
    $[21] = t10;
    $[22] = t3;
    $[23] = t5;
    $[24] = t7;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}
const CarouselDots = React.forwardRef<HTMLDivElement, CarouselDotsProps>((t0, ref) => {
  const $ = _c(23);
  let activeDotClassName;
  let className;
  let dotClassName;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      dotClassName,
      activeDotClassName,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = activeDotClassName;
    $[2] = className;
    $[3] = dotClassName;
    $[4] = props;
  } else {
    activeDotClassName = $[1];
    className = $[2];
    dotClassName = $[3];
    props = $[4];
  }
  const {
    selectedIndex,
    scrollSnaps,
    scrollTo
  } = useCarousel();
  let t1;
  if ($[5] !== className) {
    t1 = cn("flex items-center justify-center", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== activeDotClassName || $[8] !== dotClassName || $[9] !== scrollSnaps || $[10] !== scrollTo || $[11] !== selectedIndex) {
    let t3;
    if ($[13] !== activeDotClassName || $[14] !== dotClassName || $[15] !== scrollTo || $[16] !== selectedIndex) {
      t3 = (_, index) => {
        const active = index === selectedIndex;
        return <button type="button" key={index} className={carouselDotVariants()} role="tab" aria-selected={active} aria-label={`Go to slide ${index + 1}`} onClick={() => scrollTo(index)}><span aria-hidden="true" className={cn(carouselDotIndicatorVariants({
            active
          }), active ? activeDotClassName : dotClassName)} /></button>;
      };
      $[13] = activeDotClassName;
      $[14] = dotClassName;
      $[15] = scrollTo;
      $[16] = selectedIndex;
      $[17] = t3;
    } else {
      t3 = $[17];
    }
    t2 = scrollSnaps.map(t3);
    $[7] = activeDotClassName;
    $[8] = dotClassName;
    $[9] = scrollSnaps;
    $[10] = scrollTo;
    $[11] = selectedIndex;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[18] !== props || $[19] !== ref || $[20] !== t1 || $[21] !== t2) {
    t3 = <div ref={ref} className={t1} role="tablist" aria-label="Carousel pagination" {...props}>{t2}</div>;
    $[18] = props;
    $[19] = ref;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  return t3;
});
CarouselDots.displayName = "CarouselDots";
const CarouselFullscreenTrigger = React.forwardRef<HTMLButtonElement, CarouselFullscreenTriggerProps>((t0, ref) => {
  const $ = _c(12);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const {
    enterFullscreen
  } = useCarousel();
  let t1;
  if ($[4] !== children) {
    t1 = children ?? <Icon name="expand-all" className="size-5" />;
    $[4] = children;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== className || $[7] !== enterFullscreen || $[8] !== props || $[9] !== ref || $[10] !== t1) {
    t2 = <IconButton ref={ref} className={className} onClick={enterFullscreen} aria-label="Enter fullscreen" variant="white" {...props}>{t1}</IconButton>;
    $[6] = className;
    $[7] = enterFullscreen;
    $[8] = props;
    $[9] = ref;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
});
CarouselFullscreenTrigger.displayName = "CarouselFullscreenTrigger";
export { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext, CarouselNextWithProgress, CarouselProgressBar, CarouselNavButtons, CarouselDots, CarouselFullscreenTrigger, useCarousel };